import type {
  AttackTechnique,
  DataSinkKind,
  DataSourceKind,
} from "./types";

const technique = (
  id: string,
  name: string,
  tactic: AttackTechnique["tactic"],
): AttackTechnique => ({
  id,
  name,
  tactic,
  url: `https://attack.mitre.org/techniques/${id.replace(".", "/")}/`,
});

/**
 * Every technique below is checked against the current MITRE ATT&CK catalogue
 * by tests/attack-mapping.test.ts, against the reference vendored in
 * tests/fixtures/mitre-attack. An ID, a name or a tactic that drifts upstream
 * fails there rather than being printed to an analyst as current.
 */
export const ATTACK = {
  applicationToken: technique(
    "T1528",
    "Steal Application Access Token",
    "credential-access",
  ),
  passwordStores: technique(
    "T1555",
    "Credentials from Password Stores",
    "credential-access",
  ),
  browserCredentials: technique(
    "T1555.003",
    "Credentials from Web Browsers",
    "credential-access",
  ),
  unsecuredCredentials: technique(
    "T1552.001",
    "Credentials In Files",
    "credential-access",
  ),
  inputCapture: technique("T1056.001", "Keylogging", "collection"),
  clipboard: technique("T1115", "Clipboard Data", "collection"),
  screenCapture: technique("T1113", "Screen Capture", "collection"),
  processDiscovery: technique("T1057", "Process Discovery", "discovery"),
  systemChecks: technique(
    "T1497.001",
    "Virtualization/Sandbox Evasion: System Checks",
    "stealth",
  ),
  obfuscated: technique(
    "T1027",
    "Obfuscated Files or Information",
    "stealth",
  ),
  decode: technique(
    "T1140",
    "Deobfuscate/Decode Files or Information",
    "stealth",
  ),
  /**
   * ATT&CK v19 revoked T1562.001 and merged it into this technique. The
   * observable behaviour is unchanged -- a Defender or firewall weakening
   * command -- and MITRE names the successor identically, so the identifier
   * follows the rename rather than the analysis changing. Scoping it to the
   * technique instead of the concept is what the merge means.
   */
  disableOrModifyTools: technique(
    "T1685",
    "Disable or Modify Tools",
    "defense-impairment",
  ),
  fileDeletion: technique("T1070.004", "File Deletion", "stealth"),
  codeSigning: technique(
    "T1553.006",
    "Code Signing Policy Modification",
    "defense-impairment",
  ),
  scheduledTask: technique(
    "T1053.005",
    "Scheduled Task",
    "persistence",
  ),
  registryRunKeys: technique(
    "T1547.001",
    "Registry Run Keys / Startup Folder",
    "persistence",
  ),
  commandShell: technique(
    "T1059.003",
    "Windows Command Shell",
    "execution",
  ),
  powershell: technique("T1059.001", "PowerShell", "execution"),
  python: technique("T1059.006", "Python", "execution"),
  ingressTransfer: technique(
    "T1105",
    "Ingress Tool Transfer",
    "command-and-control",
  ),
  exfiltration: technique(
    "T1041",
    "Exfiltration Over C2 Channel",
    "exfiltration",
  ),
} as const;

function uniqueTechniques(techniques: AttackTechnique[]) {
  const seen = new Set<string>();
  return techniques.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function dataTypesForSource(source: DataSourceKind): string[] {
  const types: Partial<Record<DataSourceKind, string[]>> = {
    "minecraft-access-token": ["Minecraft access token"],
    "minecraft-username": ["Minecraft username"],
    "minecraft-uuid": ["Minecraft UUID"],
    "minecraft-session": ["Minecraft session"],
    "launcher-account": ["Minecraft launcher account"],
    "browser-credential": ["Browser credential"],
    "discord-token": ["Discord token"],
    environment: ["Environment variable"],
    "system-property": ["System property"],
    "file-content": ["Local file content"],
    "remote-content": ["Downloaded payload"],
    clipboard: ["Clipboard content"],
    screen: ["Screen capture"],
    keyboard: ["Keyboard input"],
    "hardcoded-value": ["Hardcoded command or configuration"],
  };
  return types[source] ?? [source.replaceAll("-", " ")];
}

export function techniquesForFlow(
  source: DataSourceKind,
  sink: DataSinkKind,
  values: string[],
): AttackTechnique[] {
  const result: AttackTechnique[] = [];
  if (
    ["minecraft-access-token", "minecraft-session", "discord-token"].includes(
      source,
    )
  ) {
    result.push(ATTACK.applicationToken);
  }
  if (source === "browser-credential") {
    result.push(ATTACK.passwordStores, ATTACK.browserCredentials);
  }
  if (source === "launcher-account" || source === "file-content") {
    result.push(ATTACK.unsecuredCredentials);
  }
  if (source === "clipboard") result.push(ATTACK.clipboard);
  if (source === "screen") result.push(ATTACK.screenCapture);
  if (source === "keyboard") result.push(ATTACK.inputCapture);
  if (
    ["http-request", "socket-write", "webhook"].includes(sink) &&
    !["remote-content", "hardcoded-value"].includes(source)
  ) {
    result.push(ATTACK.exfiltration);
  }
  if (
    source === "remote-content" &&
    ["file-write", "process-execution", "dynamic-class-load"].includes(sink)
  ) {
    result.push(ATTACK.ingressTransfer);
  }
  if (sink === "process-execution") {
    const joined = values.join(" ");
    if (/powershell|pwsh/i.test(joined)) result.push(ATTACK.powershell);
    else if (/python(?:w)?(?:\.exe)?/i.test(joined))
      result.push(ATTACK.python);
    else result.push(ATTACK.commandShell);
  }
  return uniqueTechniques(result);
}

function maskDestination(value: string) {
  return value
    .replace(
      /(discord(?:app)?\.com\/api\/webhooks\/\d+\/)[A-Za-z0-9._-]+/i,
      "$1[masked]",
    )
    .replace(/(api\.telegram\.org\/bot)\d+:[A-Za-z0-9_-]+/i, "$1[masked]");
}

export function destinationsFromValues(values: string[]) {
  const destinations: string[] = [];
  const add = (value: string) => {
    const clean = maskDestination(value)
      .replace(/[),.;'"]+$/g, "")
      .slice(0, 260);
    if (clean && !destinations.includes(clean)) destinations.push(clean);
  };
  for (const value of values) {
    for (const match of value.matchAll(/https?:\/\/[^\s"'<>\\]+/gi)) {
      add(match[0]);
    }
    if (!/https?:\/\//i.test(value)) {
      for (const match of value.matchAll(
        /\b(?:\d{1,3}\.){3}\d{1,3}(?::\d{2,5})?\b|\b(?:[a-z0-9-]+\.)+(?:com|net|org|io|dev|de|club|store|ru|cloud|app)(?::\d{2,5})?\b/gi,
      )) {
        add(match[0]);
      }
    }
    if (destinations.length >= 8) break;
  }
  return destinations;
}

/**
 * ATT&CK v19 things that no longer exist upstream, recorded so the serialized
 * report can be checked against them.
 *
 * These are here rather than in the test because the test file was reading the
 * live ATTACK export and therefore only ever saw what the module already
 * believed. A fix can satisfy every module-level test and still ship a
 * withdrawn ID or a retired tactic in a different field entirely -- which is
 * exactly what happened: the module was correct while the report object still
 * carried "defense-evasion" in action tags, a finding, and a behaviour chain
 * id, so JSON.stringify(report) contained the retired tactic four times.
 *
 * `tests/attack-mapping.test.ts` walks a real scan's serialized output and
 * fails if any of these appear anywhere in it, so the guard covers the whole
 * artifact rather than the table that produced it.
 */
export const RETIRED_ATTACK_TACTICS: readonly string[] = ["defense-evasion"];

export const WITHDRAWN_ATTACK_TECHNIQUE_IDS: readonly string[] = [
  "T1562.001",
];
